/**
 * Vista única de configuración: lista todas las plantillas y métricas
 * agrupadas por nivel con toggles inline de activo. Útil como atajo
 * cuando hay muchas definiciones y se quiere encender/apagar
 * rápidamente sin entrar a cada CRUD.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  EVAL_VISIBILITY_LABELS,
  EVAL_RECURRENCE_LABELS,
  type KpiDefinition,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

type EvalLevel = (typeof EVAL_LEVELS)[number];

interface TemplateLite {
  id: string;
  name: string;
  level: EvalLevel;
  visibility: KpiDefinition["visibility"];
  recurrence: KpiDefinition["recurrence"];
  active: boolean;
  isAnonymous: boolean;
}

export const Route = createFileRoute("/admin/evaluations-config")({
  component: EvaluationsConfigPage,
});

function EvaluationsConfigPage() {
  const qc = useQueryClient();
  const queries = useQueries({
    queries: [
      {
        queryKey: ["evaluation-templates", "all"],
        queryFn: () => api.get<{ templates: TemplateLite[] }>("/questionnaires?includeInactive=1"),
      },
      {
        queryKey: ["metric-definitions", "all"],
        queryFn: () =>
          api.get<{ definitions: KpiDefinition[] }>("/kpi-definitions?includeInactive=1"),
      },
    ],
  });

  const templatesQ = queries[0];
  const metricsQ = queries[1];

  const toggleTemplate = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/questionnaires/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["evaluation-templates"] }),
  });

  const toggleMetric = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/kpi-definitions/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metric-definitions"] }),
  });

  const isLoading = templatesQ.isLoading || metricsQ.isLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Configuración de evaluaciones"
        subtitle="Overview de plantillas y métricas, agrupadas por nivel. Toggles rápidos para activar/desactivar."
        actions={
          <Link to="/admin" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        }
      />

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {!isLoading &&
        EVAL_LEVELS.map((level) => {
          const templates = templatesQ.data?.templates.filter((t) => t.level === level) ?? [];
          const metrics = metricsQ.data?.definitions.filter((m) => m.level === level) ?? [];
          if (templates.length === 0 && metrics.length === 0) return null;

          return (
            <section key={level} className="space-y-3">
              <h2 className="font-display text-xl font-semibold">
                {EVAL_LEVEL_LABELS[level]}{" "}
                <span className="text-base-content/45 text-sm font-normal">
                  · {templates.length} encuestas · {metrics.length} métricas
                </span>
              </h2>

              {templates.length > 0 && (
                <div className="card-elevated overflow-hidden">
                  <div className="px-4 py-2 bg-base-200/40 text-xs font-semibold uppercase tracking-wider text-base-content/65">
                    Encuestas (templates)
                  </div>
                  <ul className="divide-y divide-base-300/40">
                    {templates.map((t) => (
                      <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-base-content truncate">{t.name}</span>
                            {t.isAnonymous && (
                              <span className="badge badge-warning badge-xs">anónima</span>
                            )}
                            {!t.active && (
                              <span className="badge badge-error badge-xs">inactiva</span>
                            )}
                          </div>
                          <div className="text-xs text-base-content/55 mt-0.5">
                            {EVAL_VISIBILITY_LABELS[t.visibility]} ·{" "}
                            {EVAL_RECURRENCE_LABELS[t.recurrence]}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm gap-1.5"
                          onClick={() => toggleTemplate.mutate({ id: t.id, active: !t.active })}
                          title={t.active ? "Desactivar" : "Activar"}
                        >
                          {t.active ? (
                            <Eye className="size-4 text-success" />
                          ) : (
                            <EyeOff className="size-4 text-base-content/40" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {metrics.length > 0 && (
                <div className="card-elevated overflow-hidden">
                  <div className="px-4 py-2 bg-base-200/40 text-xs font-semibold uppercase tracking-wider text-base-content/65">
                    Métricas
                  </div>
                  <ul className="divide-y divide-base-300/40">
                    {metrics.map((m) => (
                      <li key={m.id} className="px-4 py-3 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-base-content truncate">{m.name}</span>
                            {m.autoSource && (
                              <span className="badge badge-success badge-xs">
                                auto: {m.autoSource}
                              </span>
                            )}
                            {!m.active && (
                              <span className="badge badge-error badge-xs">inactiva</span>
                            )}
                          </div>
                          <div className="text-xs text-base-content/55 mt-0.5">
                            {EVAL_VISIBILITY_LABELS[m.visibility]} ·{" "}
                            {EVAL_RECURRENCE_LABELS[m.recurrence]}
                            {m.unit ? ` · ${m.unit}` : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm gap-1.5"
                          onClick={() => toggleMetric.mutate({ id: m.id, active: !m.active })}
                          title={m.active ? "Desactivar" : "Activar"}
                        >
                          {m.active ? (
                            <Eye className="size-4 text-success" />
                          ) : (
                            <EyeOff className="size-4 text-base-content/40" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
    </div>
  );
}
